/*
 * TH-51: what the UpCart drawer shows when the cart is empty.
 * Copies the server-rendered <template id="nc-empty-drawer-tpl"> (snippets/nc-empty-drawer.liquid) into
 * UpCart's shadow root under "Your cart is empty". The sale line comes from Shopify's active automatic
 * discount: one Storefront API request creating two throwaway one-item carts, one per very different product
 * (a best seller and the custom photo canvas), the same tokenless method as the PDP price test arm C. One item
 * per cart, at the product's cheapest available size, so a sale that needs 2+ items or a bigger order is not
 * announced. The line says "X% off sitewide"
 * only when both carts get an automatic percentage discount (native or app/function); otherwise it stays
 * hidden. Quotes are cached per country and probe for 10 minutes; failed quotes are not cached and hide the
 * line, and each drawer open asks again once the cache has expired.
 * The free shipping row is rendered for US visitors only. Same render for every Intelligems arm.
 */
(function () {
  'use strict';

  var API = '/api/2026-10/graphql.json';
  var CACHE_KEY = 'nc_ed_sale:v3:';
  var CACHE_MS = 10 * 60 * 1000;
  var TIMEOUT_MS = 4000;

  var ALLOC = 'discountAllocations{__typename targetType discountApplication{value{__typename ...on PricingPercentageValue{percentage}}}}';
  var CART = '{cart{' + ALLOC + ' lines(first:5){nodes{' + ALLOC + '}}}}';
  var QUERY = 'mutation NcEmptyDrawerSale($a:[CartLineInput!]!,$b:[CartLineInput!]!,$country:CountryCode)@inContext(country:$country)' +
    '{a:cartCreate(input:{lines:$a})' + CART + ' b:cartCreate(input:{lines:$b})' + CART + '}';

  var CSS = '.nc-ed{width:100%;text-align:left;padding:0 0 24px;color:#111;line-height:1.4}'
    + '.nc-ed *{box-sizing:border-box}'
    + '.nc-ed [hidden]{display:none!important}'
    + '.nc-ed-offer{margin:14px 20px 0;background:#fff7ee;border:1.5px solid #ff9c32;padding:12px 14px}'
    + '.nc-ed-sale{text-align:center}'
    + '.nc-ed-sale-title{font-size:15.5px;font-weight:700}'
    + '.nc-ed-sale-note{font-size:12px;color:#555;margin-top:2px}'
    + '.nc-ed-sale:not([hidden])+.nc-ed-ship{margin-top:10px}'
    + '.nc-ed-ship-row{display:flex;justify-content:space-between;gap:8px;font-size:12.5px}'
    + '.nc-ed-bar{height:6px;background:#e2e2e2;border-radius:3px;margin-top:6px}'
    + '.nc-ed-bar i{display:block;width:0;height:6px;background:#111;border-radius:3px}'
    + '.nc-ed-hero{position:relative;overflow:hidden;margin:14px 20px 0;background:#111;color:#fff;padding:18px 16px}'
    + '.nc-ed-tag{display:inline-block;background:#ff9c32;color:#111;font-weight:600;font-size:10.5px;letter-spacing:.4px;padding:3px 7px}'
    + '.nc-ed-hero-title{font-family:"Playfair Display",Georgia,serif;font-size:21px;line-height:1.2;font-weight:600;margin:10px 0 6px;width:66%;position:relative;z-index:1}'
    + '.nc-ed-hero-text{font-size:12.5px;line-height:1.45;color:#ddd;width:60%;margin:0}'
    + '.nc-ed-btn{display:block;width:60%;margin-top:12px;background:#ff9c32;color:#111;text-align:center;font-weight:600;font-size:13.5px;padding:11px 6px;text-decoration:none;border:0!important;outline:0;box-shadow:none;border-radius:0;position:relative;z-index:1}'
    + '.nc-ed a:focus-visible{outline:2px solid #111;outline-offset:2px}'
    + '.nc-ed .nc-ed-btn:focus-visible{outline:2px solid #fff;outline-offset:2px}'
    + '.nc-ed-hero-img{position:absolute;right:-14px;bottom:-4px;width:138px;height:138px;object-fit:contain;transform:rotate(-6deg)}'
    + '.nc-ed-h{font-size:15px;font-weight:600;margin:18px 20px 9px}'
    + '.nc-ed-row{display:flex;gap:10px;overflow-x:auto;padding:0 20px 2px;scrollbar-width:none}'
    + '.nc-ed-row::-webkit-scrollbar{display:none}'
    + '.nc-ed-card{flex:0 0 138px;border:1px solid #eee;color:#111;text-decoration:none;display:block;padding-bottom:9px}'
    + '.nc-ed-card img{display:block;width:100%;height:104px;object-fit:contain;background:#f6f6f6}'
    + '.nc-ed-card-title{display:block;font-size:12.5px;font-weight:500;line-height:1.3;padding:8px 8px 0;height:41px;overflow:hidden}'
    + '.nc-ed-link{display:block;text-align:center;margin-top:16px;font-size:14px;font-weight:500;color:#111;text-decoration:underline}';

  function root() { return window.upcartDocumentOrShadowRoot || null; }
  function template() { return document.getElementById('nc-empty-drawer-tpl'); }

  function track(name) {
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  }

  // Code-free discounts on merchandise: native automatic ones and app/function ones. Discount codes and
  // shipping discounts (targetType SHIPPING_LINE) never count.
  var AUTOMATIC = { CartAutomaticDiscountAllocation: true, CartCustomDiscountAllocation: true };

  function bestAutomaticPercent(allocs) {
    var best = 0;
    (allocs || []).forEach(function (a) {
      if (!a || !AUTOMATIC[a.__typename] || a.targetType !== 'LINE_ITEM') return;
      var v = a.discountApplication && a.discountApplication.value;
      if (v && v.__typename === 'PricingPercentageValue' && v.percentage > best) best = v.percentage;
    });
    return best;
  }

  // Automatic percentage the single item of a one-item cart gets (order-level or line-level), or null when the
  // cart is not usable.
  function cartPercent(result) {
    var cart = result && result.cart;
    var nodes = cart && cart.lines && cart.lines.nodes;
    if (!cart || !nodes || nodes.length !== 1) return null;
    return Math.max(bestAutomaticPercent(cart.discountAllocations), bestAutomaticPercent(nodes[0] && nodes[0].discountAllocations));
  }

  // Percentage both one-item carts get, so a sale on one collection only does not read as sitewide. 0 when
  // either has none. Rounded down to 0.1 so the claim never exceeds the real discount.
  // null when the response is not usable (error, throttling): such a result must not be cached.
  function parsePercent(json) {
    var d = json && json.data;
    var a = cartPercent(d && d.a);
    var b = cartPercent(d && d.b);
    if (a === null || b === null) return null;
    return Math.floor(Math.min(a, b) * 10 + 1e-6) / 10;
  }

  function readCache(key) {
    try {
      var c = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (c && typeof c.pct === 'number' && Date.now() - c.t < CACHE_MS) return c.pct;
    } catch (e) {}
    return null;
  }
  function writeCache(key, pct) {
    try { window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), pct: pct })); } catch (e) {}
  }

  // Resolves to the percentage (0 = no sale), or null when there is no usable answer this time.
  var pending = {};
  function salePercent(variantIds) {
    function line(id) { return [{ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 }]; }
    var S = window.Shopify || {};
    var country = S.country || 'US';
    var key = CACHE_KEY + country + ':' + variantIds.join(',');
    var cached = readCache(key);
    if (cached !== null) return Promise.resolve(cached);
    if (pending[key]) return pending[key];
    // A quote given up on at the timeout is cancelled and never written to the cache, so the next open asks
    // again and a late answer cannot overwrite a newer one.
    var timer = null;
    var timedOut = false;
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var request = fetch(API, {
      method: 'POST',
      credentials: 'omit',
      signal: ctrl ? ctrl.signal : undefined,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query: QUERY,
        variables: { a: line(variantIds[0]), b: line(variantIds[1]), country: country }
      })
    }).then(function (r) { return r.json(); }).then(function (json) {
      if (timedOut) return null;
      var pct = parsePercent(json);
      if (pct !== null) writeCache(key, pct);
      return pct;
    });
    var timeout = new Promise(function (resolve) {
      timer = setTimeout(function () { timedOut = true; if (ctrl) ctrl.abort(); resolve(null); }, TIMEOUT_MS);
    });
    pending[key] = Promise.race([request, timeout])
      .catch(function () { return null; })
      .then(function (pct) { clearTimeout(timer); delete pending[key]; return pct; });
    return pending[key];
  }

  function fill(box) {
    var offer = box.querySelector('.nc-ed-offer');
    var sale = box.querySelector('.nc-ed-sale');
    var ship = box.querySelector('[data-nc-ed-us-only]');
    var country = (window.Shopify && window.Shopify.country) || 'US';
    if (ship && country === 'US') ship.hidden = false;
    function sync() { if (offer) offer.hidden = !((ship && !ship.hidden) || (sale && !sale.hidden)); }
    sync();
    var probe = box.querySelector('.nc-ed-probe');
    var ids = probe ? [probe.getAttribute('data-variant'), probe.getAttribute('data-variant2')] : [];
    // "Sitewide" needs two different probed products; with one missing the line stays hidden.
    if (!sale || !ids[0] || !ids[1] || ids[0] === ids[1]) return;
    salePercent(ids).then(function (pct) {
      if (!box.isConnected) return;
      // Only a confirmed sale shows the line. No usable answer (null) hides it until a later open gets one.
      var on = pct !== null && pct > 0;
      var title = sale.querySelector('.nc-ed-sale-title');
      var text = pct + '% off sitewide';
      if (on && title.textContent !== text) title.textContent = text;
      sale.hidden = !on;
      sync();
    });
  }

  function inject() {
    var r = root();
    var tpl = template();
    if (!r || !tpl) return;
    var body = r.querySelector('[class*="CartPreview__empty__body"]');
    if (!body || body.querySelector('.nc-ed')) return;
    if (!r.querySelector('#nc-ed-style')) {
      var st = document.createElement('style');
      st.id = 'nc-ed-style';
      st.textContent = CSS;
      r.appendChild(st);
    }
    var frag = document.importNode(tpl.content, true);
    var box = frag.querySelector('.nc-ed');
    if (!box) return;
    box.addEventListener('click', function (e) {
      var a = e.target && e.target.closest && e.target.closest('[data-nc-ed-click]');
      if (a) track('nc_empty_drawer_click_' + a.getAttribute('data-nc-ed-click'));
    });
    body.appendChild(frag);
    fill(box);
  }

  // UpCart pre-renders its empty state while the drawer is closed, so a view is counted only when the drawer
  // opens with the block in it. The open also refills a block injected earlier, so a failed quote is retried
  // and an expired one re-checked while the page stays open.
  function onOpened() {
    schedule();
    var r0 = root();
    var existing = r0 && r0.querySelector('.nc-ed');
    if (existing) fill(existing);
    setTimeout(function () {
      var r = root();
      if (r && r.querySelector('.nc-ed')) track('nc_empty_drawer_view');
    }, 600);
  }

  var t = null;
  function schedule() { clearTimeout(t); t = setTimeout(inject, 150); }

  function start() {
    var r = root(); if (!r) return false;
    try { new MutationObserver(schedule).observe(r, { childList: true, subtree: true }); } catch (e) {}
    ['upcartSubscribeCartLoaded', 'upcartSubscribeCartUpdated'].forEach(function (fn) {
      if (typeof window[fn] === 'function') { try { window[fn](schedule); } catch (e) {} }
    });
    if (typeof window.upcartSubscribeCartOpened === 'function') { try { window.upcartSubscribeCartOpened(onOpened); } catch (e) {} }
    schedule();
    return true;
  }

  window.NCEmptyDrawer = { parsePercent: parsePercent, refresh: schedule };

  var tries = 0;
  var poll = setInterval(function () {
    if (window.upcartDocumentOrShadowRoot && start()) { clearInterval(poll); return; }
    if (++tries > 100) clearInterval(poll);
  }, 300);
})();
